import { createTheme, PaletteMode, Theme } from '@mui/material';

export type weekdayColor = {
  main: string;
  border: string;
  underline?: string;
  light: string;
};

export type activityColors = {
  green: string;
  purple: string;
  blue: string;
  red: string;
  yellow: string;
  turquoise: string;
};

declare module '@mui/material/styles/createPalette' {
  // eslint-disable-next-line
  interface Palette {
    line: string;
    lineLight: string;
    lineDark: string;
    white: string;
    black: string;
    weekdays: {
      monday: weekdayColor;
      tuesday: weekdayColor;
      wednesday: weekdayColor;
      thursday: weekdayColor;
      friday: weekdayColor;
      saturday: weekdayColor;
      sunday: weekdayColor;
    };
    activities: activityColors;
    backdrop: string;
    child?: childPaletteTokens;
    teen?: teenPaletteTokens;
  }
}

// Barntema-tokens (prototypens palett som saknar MUI-standardnycklar).
// Valfri i palette (bara definierad i childPalette) → vuxentemat påverkas ej.
export type childPaletteTokens = {
  bg: string;
  card: string;
  text: string;
  textSoft: string;
  border: string;
  primaryLight: string;
  greenLight: string;
  amberLight: string;
  orange: string;
  orangeLight: string;
  redLight: string;
  // Skamfri statusmodell (Pipeline 1): neutrala/uppmärksammande tokens som
  // ersätter den röda "MISSAD!"-tonen. 'muted'/'mutedLight' = neutral grund
  // (ex. nollställd streak), 'attentionMain'/'attentionLight' = mjuk bärnsten
  // för missade aktiviteter (ej rött), 'alert' reserveras för faktiska fel.
  muted: string;
  mutedLight: string;
  attentionMain: string;
  attentionLight: string;
  alert: string;
  shadow: string;
};

const lightPalette = {
  mode: 'light' as PaletteMode,
  primary: { main: '#006151' },
  secondary: { main: '#f6f8fe' },
  error: { main: '#E53935' },
  warning: { main: '#FACC15' },
  success: { main: '#00C566' },
  weekdays: {
    monday: { main: '#70A560', border: 'none', light: '#e9f8e4' },
    tuesday: { main: '#5B92D1', border: 'none', light: '#dee9f6' },
    wednesday: {
      main: '#ffffff',
      border: '2px solid lightgray',
      underline: '#cacaca',
      light: '#f6f6f6',
    },
    thursday: { main: '#C78F46', border: 'none', light: '#fff1dd' },
    friday: { main: '#F4EA2E', border: 'none', light: '#fffdd0' },
    saturday: { main: '#FFA0B2', border: 'none', light: '#ffecef' },
    sunday: { main: '#FB5050', border: 'none', light: '#ffeded' },
  },
  moods: {
    bad: {
      main: '#eb2c3a',
    },
    2: {
      main: '#ee8130',
    },
    3: { main: '#fce92b' },
    4: { main: '#91bd49' },
    5: { main: '#009f54' },
  },
  activities: {
    green: '#1F8900',
    purple: '#7338BE',
    blue: '#2873DB',
    red: '#D1435B',
    yellow: '#D7C30F',
    turquoise: '#078484',
  },
  white: '#fefefe',
  lineLight: '#ECF1F6',
  line: '#e3e7ec',
  lineDark: '#4a4a65',
  black: '#111111',
  calendarLine: '#eeeeee',
  googleWhite: { main: '#fff' },
  appleBlack: { main: '#000 ' },
  backdrop: '#f8f8f8',
};

//Mostly AI-generated, but looks good enough
const darkPalette = {
  mode: 'dark' as PaletteMode,
  primary: { main: '#00e6c8' }, // Adjusted primary color for better contrast
  secondary: { main: '#18191A' }, // Darker secondary color for dark mode
  error: { main: '#EF5350' }, // Darker error color
  warning: { main: '#FFD54F' }, // Darker warning color
  success: { main: '#66BB6A' }, // Adjusted success color for better contrast
  background: { default: '#121212', paper: '#2b2b2b' }, // Darker background color
  weekdays: {
    monday: { main: '#70A560', border: 'none', light: '#364038' }, // Adjusted light color for better contrast
    tuesday: { main: '#5B92D1', border: 'none', light: '#1E2E4A' }, // Adjusted light color for better contrast
    wednesday: {
      main: '#ffffff', // Darker color for Wednesday
      light: '#292A2B', // Darker light color
    },
    thursday: { main: '#C78F46', border: 'none', light: '#503C19' }, // Adjusted light color for better contrast
    friday: { main: '#F4EA2E', border: 'none', light: '#4E4817' }, // Adjusted light color for better contrast
    saturday: { main: '#FFA0B2', border: 'none', light: '#5D2A39' }, // Adjusted light color for better contrast
    sunday: { main: '#FB5050', border: 'none', light: '#5A1B1B' }, // Adjusted light color for better contrast
  },
  activities: {
    green: '#1F8900',
    purple: '#7338BE',
    blue: '#2873DB',
    red: '#D1435B',
    yellow: '#D7C30F',
    turquoise: '#078484',
  },
  white: '#18191A', // Darker white color for dark mode
  lineLight: '#2C2D2E', // Darker lineLight color
  line: '#333333', // Darker line color
  lineDark: '#888888', // Darker lineDark color
  black: '#FDFDFD', // Lighter black color for better contrast
  calendarLine: '#eeeeee',
  googleWhite: { main: '#fff' }, // Google white remains the same
  appleBlack: { main: '#000 ' }, // Apple black remains the same
  backdrop: '#0e0e0e',
};

// NPF child-mode palette: warm, calm variant of the light palette.
// Primär byts till en #4A90C4-familj. childPalette definierar avsiktligt
// shade-nycklarna primary.50/100 och grey.50/100/300 för att ge röstvyns
// ClarificationCard en barnvänlig nyans. I vuxentemat är dessa odefinierade och
// MUI faller då tillbaka på sina defaults (deep-merge) utan att krascha.
//
// Prototypens palett (Todej ny ux.html) mappas in här så att barn-vyerna ALDRIG
// hårdkodar hex. Konsumenter når dem via theme.palette.{primary|success|warning}
// samt theme.palette.child.* (custom). primary = #4A90C4, success = #5AAF78
// (green), warning = #D4A843 (amber). child.* bär de färgnyanser som prototypen
// använder men som inte ryms i MUI:s standardnycklar (bg, card, text, soft m.fl.).
const childPalette = {
  ...lightPalette,
  mode: 'light' as PaletteMode,
  primary: {
    main: '#4A90C4',
    light: '#EBF4FB',
    dark: '#357AA8',
    contrastText: '#ffffff',
    50: '#EAF3F9',
    100: '#D2E4F1',
  },
  success: {
    main: '#5AAF78',
    light: '#EAF5EF',
    dark: '#4A9A67',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#D4A843',
    light: '#FBF4E0',
    dark: '#B88E2E',
    contrastText: '#ffffff',
  },
  error: {
    main: '#D95F5F',
    light: '#FAEAEA',
    contrastText: '#ffffff',
  },
  secondary: { main: '#FBF6EF', contrastText: '#2D3142' },
  grey: {
    50: '#FAF7F2',
    100: '#F1ECE4',
    300: '#D9D2C6',
  },
  background: { default: '#FAF8F4', paper: '#FFFFFF' },
  text: { primary: '#2D3142', secondary: '#6B7280' },
  // Custom barntema-nycklar (prototypens tokens som saknar MUI-motsvarighet).
  child: {
    bg: '#FAF8F4',
    card: '#FFFFFF',
    text: '#2D3142',
    textSoft: '#6B7280',
    border: '#E8E4DE',
    primaryLight: '#EBF4FB',
    greenLight: '#EAF5EF',
    amberLight: '#FBF4E0',
    orange: '#E8924A',
    orangeLight: '#FDF0E6',
    redLight: '#FAEAEA',
    // Skamfri statusmodell (Pipeline 1). attentionMain = bärnsten #8C6020:
    // WCAG AA som text mot attentionLight (5.02:1) och mot card #FFFFFF (5.51:1).
    muted: '#8A8578',
    mutedLight: '#F1ECE4',
    attentionMain: '#8C6020',
    attentionLight: '#FBF4E0',
    alert: '#D95F5F',
    shadow: '0 2px 12px rgba(45,49,66,0.08)',
  },
  backdrop: '#FAF8F4',
};

// NPF child-mode MÖRKT läge (Pipeline 2). Mall = teenPaletteDark, men barnets
// VARMARE ton: jordnära mörk (ingen ren svart/vit) som behåller den lugna,
// skamfria känslan från ljusa barntemat. WCAG AA verifierat (childPaletteDark):
//   attentionMain #D9B05C  vs attentionLight #2C2615 = 7.39:1, vs card #262320 = 7.68:1
//   text.primary  #ECE8E1  vs card #262320 = 12.80:1, vs bg #1C1A17 = 14.22:1
// Symmetriskt med teenPaletteDark: sätter child-blocket (som barn-vyerna läser
// via useChildTokens) samt MUI:s standardnycklar. Inga teen-/young-nycklar här.
const childPaletteDark = {
  ...darkPalette,
  mode: 'dark' as PaletteMode,
  primary: {
    main: '#6FB0DC',
    light: '#23303A',
    dark: '#5A97C0',
    contrastText: '#16140F',
    50: '#1F2A30',
    100: '#23303A',
  },
  success: {
    main: '#6FB98A',
    light: '#1E2A22',
    dark: '#5AAF78',
    contrastText: '#16140F',
  },
  warning: {
    main: '#D9B05C',
    light: '#2C2615',
    dark: '#C49A4A',
    contrastText: '#16140F',
  },
  error: {
    main: '#D98585',
    light: '#2C1A1A',
    contrastText: '#16140F',
  },
  secondary: { main: '#262320', contrastText: '#ECE8E1' },
  background: { default: '#1C1A17', paper: '#262320' },
  text: { primary: '#ECE8E1', secondary: '#A8A296' },
  child: {
    bg: '#1C1A17',
    card: '#262320',
    text: '#ECE8E1',
    textSoft: '#A8A296',
    border: '#3A352F',
    primaryLight: '#23303A',
    greenLight: '#1E2A22',
    amberLight: '#2C2615',
    orange: '#E0A468',
    orangeLight: '#2E2419',
    redLight: '#2C1A1A',
    // Skamfri statusmodell (Pipeline 1), barn-dark. attentionMain = #D9B05C:
    // WCAG AA mot attentionLight #2C2615 (7.39:1) och card #262320 (7.68:1).
    muted: '#7C766B',
    mutedLight: '#2B2724',
    attentionMain: '#D9B05C',
    attentionLight: '#2C2615',
    alert: '#D98585',
    shadow: '0 2px 12px rgba(0,0,0,0.45)',
  } as childPaletteTokens,
  white: '#262320',
  lineLight: '#211E1B',
  line: '#2B2724',
  lineDark: '#A8A296',
  black: '#ECE8E1',
  backdrop: '#1C1A17',
};

const getPalette = (mode: PaletteMode, childMode = false) =>
  childMode ? childPalette : mode === 'dark' ? darkPalette : lightPalette;

const appTHeme = {
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h3: 'h2',
          h4: 'h2',
          h5: 'h2',
          h6: 'h2',
          subtitle1: 'p',
          subtitle2: 'p',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: '1em',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            margin: 0,
          },
          '&:before': {
            display: 'none',
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: 'unset',
          },
        },
        content: {
          '&.Mui-expanded': {
            margin: '12px 0',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'unset' } },
    },
  },
};

// NPF child-mode theme overrides. Additivt: appTHeme (vuxen) lämnas helt orörd.
// Nunito laddas dynamiskt i App.tsx endast när childMode är aktivt.
const childTheme = {
  ...appTHeme,
  typography: {
    fontFamily: 'Nunito, Roboto, Helvetica, Arial, sans-serif',
    fontSize: 14,
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
  },
  components: {
    ...appTHeme.components,
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          borderRadius: '1.25em',
          minHeight: 48,
        },
      },
    },
  },
};

// buildChildTheme(mode): bygger MUI-temat för barn i valt läge. Mörkt läge stöds
// (Pipeline 2). Symmetriskt med buildTeenTheme. childPalette
// (ljust) är byte-identiskt med tidigare; mörkt väljer childPaletteDark.
const buildChildTheme = (mode: PaletteMode): Theme =>
  createTheme({
    ...childTheme,
    palette: mode === 'dark' ? childPaletteDark : childPalette,
  });

// ─────────────────────────────────────────────────────────────────────────────
// NPF UNGDOM (Teen). Additivt: lightPalette/darkPalette/childPalette/childTheme/
// getPalette ovan lämnas helt orörda. Teen ärver barnets komponentarkitektur men
// med svalare/dämpade färger, Inter-typografi och stöd för mörkt läge.
// Exakta hex hämtade ur DESIGN-FORSLAG-NPF-UNGDOM.md §5.
// ─────────────────────────────────────────────────────────────────────────────

// Teen-tokens = barnets token-uppsättning + teen-specifik 'accent'. De
// semantiska statusfärgerna muted/mutedLight/attentionMain/attentionLight/alert
// är nu del av childPaletteTokens (skamfri statusmodell, Pipeline 1) och delas
// därmed av barn och teen — teen lägger bara till sin svala accentfärg.
export type teenPaletteTokens = childPaletteTokens & {
  accent: string;
};

// KRITISKT (R3): teen-paletten sätter BÅDE child-blocket (med teen-värden) OCH
// ett teen-block. Iteration 1 ärver barn-vyerna, som läser theme.palette.child
// via useChildTokens — genom att fylla child med teen-färger får de ärvda
// vyerna teen-temat utan att en enda barn-vy behöver röras.
const teenPaletteLight = {
  ...lightPalette,
  mode: 'light' as PaletteMode,
  primary: {
    main: '#3D5A80',
    light: '#E7ECF2',
    dark: '#2C4360',
    contrastText: '#ffffff',
    50: '#EEF1F5',
    100: '#E7ECF2',
  },
  success: {
    main: '#5C9472',
    light: '#E8F0EB',
    dark: '#4A7B5E',
    contrastText: '#ffffff',
  },
  warning: {
    main: '#C49A4A',
    light: '#F6EFDD',
    dark: '#A8823A',
    contrastText: '#ffffff',
  },
  error: {
    main: '#C25B5B',
    light: '#F4E5E5',
    contrastText: '#ffffff',
  },
  secondary: { main: '#E7ECF2' },
  grey: {
    50: '#F4F5F7',
    100: '#EEF0F3',
    300: '#DDE1E7',
  },
  background: { default: '#F4F5F7', paper: '#FFFFFF' },
  text: { primary: '#252A33', secondary: '#6B7280' },
  child: {
    bg: '#F4F5F7',
    card: '#FFFFFF',
    text: '#252A33',
    textSoft: '#6B7280',
    border: '#DDE1E7',
    primaryLight: '#E7ECF2',
    greenLight: '#E8F0EB',
    amberLight: '#F6EFDD',
    orange: '#C49A4A',
    orangeLight: '#F6EFDD',
    redLight: '#F4E5E5',
    // Skamfri statusmodell (Pipeline 1), teen-light. attentionMain = #8F6427:
    // WCAG AA mot attentionLight #F6EFDD (4.55:1) och card #FFFFFF (5.22:1).
    muted: '#8A93A3',
    mutedLight: '#EEF0F3',
    attentionMain: '#8F6427',
    attentionLight: '#F6EFDD',
    alert: '#C25B5B',
    shadow: '0 1px 8px rgba(37,42,51,0.07)',
  } as teenPaletteTokens,
  teen: {
    bg: '#F4F5F7',
    card: '#FFFFFF',
    text: '#252A33',
    textSoft: '#6B7280',
    border: '#DDE1E7',
    primaryLight: '#E7ECF2',
    greenLight: '#E8F0EB',
    amberLight: '#F6EFDD',
    orange: '#C49A4A',
    orangeLight: '#F6EFDD',
    redLight: '#F4E5E5',
    muted: '#8A93A3',
    mutedLight: '#EEF0F3',
    attentionMain: '#8F6427',
    attentionLight: '#F6EFDD',
    alert: '#C25B5B',
    shadow: '0 1px 8px rgba(37,42,51,0.07)',
    accent: '#5E8B7E',
  } as teenPaletteTokens,
  backdrop: '#F4F5F7',
};

const teenPaletteDark = {
  ...darkPalette,
  mode: 'dark' as PaletteMode,
  primary: {
    main: '#6E8FB8',
    light: '#2C323D',
    dark: '#56749B',
    contrastText: '#0E1116',
    50: '#1F242E',
    100: '#2C323D',
  },
  success: {
    main: '#6FA886',
    light: '#1F2A24',
    dark: '#5C9472',
    contrastText: '#0E1116',
  },
  warning: {
    main: '#D2AC63',
    light: '#2C2616',
    dark: '#C49A4A',
    contrastText: '#0E1116',
  },
  error: {
    main: '#C25B5B',
    light: '#2C1B1B',
    contrastText: '#0E1116',
  },
  secondary: { main: '#1F242E' },
  grey: {
    50: '#1F242E',
    100: '#2C323D',
    300: '#3A4150',
  },
  background: { default: '#161A21', paper: '#1F242E' },
  text: { primary: '#E8EAED', secondary: '#9AA3B2' },
  child: {
    bg: '#161A21',
    card: '#1F242E',
    text: '#E8EAED',
    textSoft: '#9AA3B2',
    border: '#2C323D',
    primaryLight: '#2C323D',
    greenLight: '#1F2A24',
    amberLight: '#2C2616',
    orange: '#D2AC63',
    orangeLight: '#2C2616',
    redLight: '#2C1B1B',
    // Skamfri statusmodell (Pipeline 1), teen-dark. attentionMain = #D2AC63:
    // WCAG AA mot attentionLight #2C2616 (7.04:1) och card #1F242E (7.27:1).
    muted: '#6E7686',
    mutedLight: '#252A33',
    attentionMain: '#D2AC63',
    attentionLight: '#2C2616',
    alert: '#C25B5B',
    shadow: '0 1px 8px rgba(0,0,0,0.4)',
  } as teenPaletteTokens,
  teen: {
    bg: '#161A21',
    card: '#1F242E',
    text: '#E8EAED',
    textSoft: '#9AA3B2',
    border: '#2C323D',
    primaryLight: '#2C323D',
    greenLight: '#1F2A24',
    amberLight: '#2C2616',
    orange: '#D2AC63',
    orangeLight: '#2C2616',
    redLight: '#2C1B1B',
    muted: '#6E7686',
    mutedLight: '#252A33',
    attentionMain: '#D2AC63',
    attentionLight: '#2C2616',
    alert: '#C25B5B',
    shadow: '0 1px 8px rgba(0,0,0,0.4)',
    accent: '#6FA886',
  } as teenPaletteTokens,
  white: '#1F242E',
  lineLight: '#252A33',
  line: '#2C323D',
  lineDark: '#9AA3B2',
  black: '#E8EAED',
  backdrop: '#161A21',
};

// Teen-typografi (§6): Inter ersätter barnets Nunito. Fallback identisk med barn.
// NPF-luft behålls (lineHeight 1.6). Knappvikt 600 för stramare intryck.
const teenTheme = {
  ...childTheme,
  typography: {
    ...childTheme.typography,
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

// buildTeenTheme(mode): bygger MUI-temat för teen i valt läge. Mörkt läge stöds.
const buildTeenTheme = (mode: PaletteMode): Theme =>
  createTheme({
    ...teenTheme,
    palette: mode === 'dark' ? teenPaletteDark : teenPaletteLight,
  });

export {
  appTHeme,
  childTheme,
  childPalette,
  childPaletteDark,
  buildChildTheme,
  getPalette,
  teenTheme,
  teenPaletteLight,
  teenPaletteDark,
  buildTeenTheme,
};
